/**
 * raia-api, called directly from the browser.
 *
 * This replaces agent/internal/upload/queue.go, and with it the entire Google
 * Drive for Desktop hop. The backend already had the path for it: with
 * `drive_path` omitted, `PhotoSessionService.CreateSession` creates and shares
 * the session folder itself instead of resolving one the sync client uploaded,
 * and `POST /v1/photo-session/:key/file` streams the photos in. That branch was
 * written for "a machine with no Drive for Desktop", which is now every machine.
 *
 * What that changes, and what it does not:
 *
 *  - The venue still pays for ONE upload. It used to be Drive for Desktop
 *    sending the bytes and the agent only asking for the share; now the bytes
 *    go to raia-api, which puts them on Drive server-side.
 *  - The whole `DrivePathFor` / "My Drive" class of failure is gone. A target
 *    folder outside the Drive mount used to mean the guest's QR never resolved.
 *  - `ErrNotSynced` is gone with it. The folder exists the moment this returns,
 *    so there is no "wait for the sync to catch up" retry any more — only the
 *    ordinary retry of a request that failed on venue wifi.
 *
 * The short code is still derived from the session key offline and sent along,
 * so the printed QR is correct before any of this runs.
 */

export class RaiaError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'RaiaError'
  }
}

export interface RaiaAccount {
  baseUrl: string
  email: string
  password: string
}

export interface CreatedSession {
  folderId: string
  folderLink: string
}

/**
 * The bearer token, cached in memory only.
 *
 * raia-api issues 60-minute JWTs and has no refresh endpoint, so a long evening
 * outlives one; re-login is the only option, exactly as in the Go queue. Kept
 * out of IndexedDB deliberately — it buys nothing over re-logging in and would
 * be one more credential sitting on a borrowed venue PC.
 */
let token: string | null = null

export function forgetToken(): void {
  token = null
}

function base(account: RaiaAccount): string {
  return account.baseUrl.replace(/\/+$/, '')
}

async function login(account: RaiaAccount, signal?: AbortSignal): Promise<string> {
  if (token) return token

  const res = await fetch(`${base(account)}/v1/user/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: account.email, password: account.password }),
    signal,
  })

  if (!res.ok) throw new RaiaError(`Login gagal: ${res.status} ${res.statusText}`, res.status)

  const parsed = (await res.json()) as { data?: { token?: string } }
  const issued = parsed.data?.token
  if (!issued) throw new RaiaError('Login tidak mengembalikan token', res.status)

  token = issued
  return issued
}

/** Sends an authenticated request, re-logging in once on a 401. */
async function authed(
  account: RaiaAccount,
  build: (token: string) => Request,
  signal?: AbortSignal,
): Promise<Response> {
  const res = await fetch(build(await login(account, signal)))
  if (res.status !== 401) return res

  forgetToken()
  return fetch(build(await login(account, signal)))
}

async function fail(res: Response, what: string): Promise<never> {
  const detail = await res.text().catch(() => '')
  throw new RaiaError(`${what}: ${res.status} ${detail.slice(0, 200)}`, res.status)
}

/**
 * Creates and shares the session's Drive folder and registers the short code.
 *
 * `drive_path` is deliberately not sent. Sending it would ask the backend to
 * find a folder Drive for Desktop had uploaded, which is the model this port
 * removes; omitting it takes the branch that creates the folder outright and
 * returns its id immediately, so there is nothing to wait for.
 *
 * Safe to call twice: the backend looks for an existing folder of the same name
 * before creating one, so a retried session does not orphan photos in a first.
 */
export async function createSession(
  account: RaiaAccount,
  sessionKey: string,
  shortCode: string,
  signal?: AbortSignal,
): Promise<CreatedSession> {
  const res = await authed(
    account,
    (bearer) =>
      new Request(`${base(account)}/v1/photo-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
        body: JSON.stringify({ session_key: sessionKey, short_url: shortCode }),
        signal,
      }),
    signal,
  )

  if (!res.ok) await fail(res, 'Tidak bisa membuat sesi')

  const parsed = (await res.json()) as {
    data?: { folder_id?: string; folder_link?: string }
  }
  if (!parsed.data?.folder_id) throw new RaiaError('Sesi dibuat tanpa folder id', res.status)

  return { folderId: parsed.data.folder_id, folderLink: parsed.data.folder_link ?? '' }
}

/** Streams one photo into the session folder. */
export async function uploadFile(
  account: RaiaAccount,
  sessionKey: string,
  name: string,
  file: Blob,
  signal?: AbortSignal,
): Promise<void> {
  const body = new FormData()
  body.append('file', file, name)

  const res = await authed(
    account,
    (bearer) =>
      new Request(`${base(account)}/v1/photo-session/${encodeURIComponent(sessionKey)}/file`, {
        method: 'POST',
        // No Content-Type: the boundary has to come from FormData itself.
        headers: { Authorization: `Bearer ${bearer}` },
        body,
        signal,
      }),
    signal,
  )

  if (!res.ok) await fail(res, `Tidak bisa mengunggah ${name}`)
}

/** Verifies the account from Settings, so a bad password is found before an event. */
export async function verifyAccount(account: RaiaAccount): Promise<void> {
  forgetToken()
  await login(account)
}
